import { AfterViewInit, Component, ElementRef, EventEmitter, Input, NgZone, OnChanges, OnDestroy, OnInit, Output, SimpleChanges, ViewChild } from '@angular/core';
import { catchError, Subscription, throwError } from 'rxjs';
import { ChartComponent } from 'ng-apexcharts';
import { ChartConfigService } from 'src/app/core/services/utelize/general/chart-config-service';
import { DeviceVolumeAndHealthTrendDTO } from 'src/app/core/models/system/widget/widget-device-volume-and-health-trend.dto';
import { ExceptionsService } from 'src/app/core/services/utelize/general/exceptions-service';
import { FlatpickrFormat, MdmDeviceClass } from 'src/app/core/consts';
import { OurDate } from 'src/app/core/interfaces/general/our-date';
import { mdmUserDomainsDisplayText } from 'src/app/core/functions-generic/functions-generic-mdm';
import { populateOurDate } from 'src/app/core/functions-generic/functions-generic-date';
import { TippyToolTipService } from 'src/app/core/services/utelize/general/tippy-tool-tip-service';
import { WidgetService } from 'src/app/core/services/utelize/general/widget-service';

const seriesMdmCompliancePct    ="MDM Compliance %";
const seriesOsMajorCompliancePct="OS Major Compliance %";
const seriesOsMinorCompliancePct="OS Minor Compliance %";
const compliancePctSeries       =[seriesMdmCompliancePct, seriesOsMajorCompliancePct, seriesOsMinorCompliancePct];
const mobileOnlySeries          =[seriesOsMajorCompliancePct, seriesOsMinorCompliancePct];
const countSeries               =['Device Count','> 1 Device','> 1 Registration','Checked In (30d)','Enrolled (7d)','Not Checked In (30d)','Not Supported (EOL)','Over 4 Years','Storage Threshold','Supported (EOL)'];

@Component({
  selector: 'app-chart-mdm-device-volume-and-health-trend',
  templateUrl: './chart-mdm-device-volume-and-health-trend.component.html',
  styleUrls: ['./chart-mdm-device-volume-and-health-trend.component.scss'],
  providers: [TippyToolTipService]
})

export class ChartMdmDeviceVolumeAndHealthTrendComponent implements AfterViewInit, OnChanges, OnDestroy, OnInit {

  @Input() allowContainerClose: boolean | undefined;
  @Input() chartHeight        : number  | undefined;
  @Input() chartTitle         : string  | undefined;
  @Input() containerGrow      : boolean | undefined;
  @Input() deviceClass        : string  | undefined;
  @Input() marginBottom       : string  | undefined="mb-0";
  @Input() showTitleHintText  : boolean | undefined;
  @Input() userDomains        : string  | undefined;

  @Output() containerClosed : EventEmitter<void>=new EventEmitter<void>();
  @Output() userDomainsClear: EventEmitter<void>=new EventEmitter<void>();

  @ViewChild('chartHost') chartHost!: ElementRef<HTMLElement>;
  @ViewChild('chartRef' ) chartRef! : ChartComponent;

  get isOtherClass   () { return this.deviceClass===MdmDeviceClass.other                            ; }
  get userDomainsText() { return mdmUserDomainsDisplayText(this.exceptionsService, this.userDomains); }

  private buildChart               : boolean=true;
  private filterCompanyID          : number  | undefined;
  private sourceData               : DeviceVolumeAndHealthTrendDTO[]=[];
  private subVolumeAndHealthTrend! : Subscription | undefined;
  private widgetCategories         : string[]=[];
  private widgetCheckedIn30D       : number[]=[];
  private widgetDeviceCount        : number[]=[];
  private widgetEnrolledLast7D     : number[]=[];
  private widgetMdmCompliant       : (number | null)[]=[];
  private widgetMdmCompliantPct    : (number | null)[]=[];
  private widgetMoreThan1Dev       : number[]=[];
  private widgetMoreThan1Reg       : number[]=[];
  private widgetNotChecked30D      : number[]=[];
  private widgetNotSupportedEol    : number[]=[];
  private widgetOsMajorCompliant   : (number | null)[]=[];
  private widgetOsMajorCompliantPct: (number | null)[]=[];
  private widgetOsMinorCompliant   : (number | null)[]=[];
  private widgetOsMinorCompliantPct: (number | null)[]=[];
  private widgetOver4Years         : number[]=[];
  private widgetStorageThresh      : number[]=[];
  private widgetSupportedEol       : number[]=[];

  public dateFormat               : string=FlatpickrFormat.Day_Month_Year;
  public expandContainer          : boolean=true;
  public filterDateFrom           : OurDate | undefined;
  public filterDateTo             : OurDate | undefined;
  public hasComplianceData        : boolean=false;
  public hiddenSeries             : string[]=[];
  public hoveredSeriesName        : string | undefined;
  public legendItems              : { color: string, name: string }[]=[];
  public noData                   : boolean=false;
  public quickRangeDays           : number  | undefined;
  public visibleContainer         : boolean=true;
  public visibleLegendItems       : { color: string, name: string }[]=[];
  public volumeAndHealthTrendChart: any | undefined;

  constructor(
    private chartConfigService : ChartConfigService,
    private exceptionsService  : ExceptionsService,
    private ngZone             : NgZone,
    private tippyToolTipService: TippyToolTipService,
    private widgetService      : WidgetService
  ) {

  }

  public ngOnInit(): void {
    try {
      if (this.chartHeight==undefined)
        this.chartHeight=380;

      if (this.chartTitle==undefined)
        this.chartTitle="Device Volume & Health Trend";
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }

  public ngAfterViewInit(): void {
    try {
      this.buildChart=true;

      this.tippyToolTipService.setupBootstrapTooltips('[data-bs-toggle="tooltip"]', 'left');
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }

  public ngOnChanges(changes: SimpleChanges): void {
    try {
      if (((changes['deviceClass'] && !changes['deviceClass'].firstChange)
           || (changes['userDomains'] && !changes['userDomains'].firstChange)
          ) && (this.filterCompanyID ?? -1)!=-1)
        this.getVolumeAndHealthTrend();
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }

  public ngOnDestroy(): void {
    try {
      this.unsubscribeVolumeAndHealthTrendChart();
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }

  public getCompanyVolumeAndHealthTrendChart(filterCompanyID: number | undefined, _filterDate: OurDate | undefined) {
    try {
      if (filterCompanyID==undefined)
        throw new Error("ChartMdmDeviceVolumeAndHealthTrendComponent - CompanyID is undefined");

      this.filterCompanyID=filterCompanyID;

      this.getVolumeAndHealthTrend();
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }

  public getVolumeAndHealthTrend() {
    try {
      this.unsubscribeVolumeAndHealthTrendChart();
      this.subVolumeAndHealthTrend=new Subscription();

      const companyID=<number>(this.filterCompanyID ?? 0);

      const subUpdates=this.widgetService.getIntuneDeviceVolumeAndHealthTrendUpdates(companyID, this.deviceClass, this.userDomains)
      .pipe(
        catchError(error =>
          throwError(() => new Error(this.exceptionsService.formatHttpError(error)))
        )
      )
      .subscribe({
        next : data => this.updateChart(data),
        error: error => this.exceptionsService.handleException(new Error(error))
      });

      this.subVolumeAndHealthTrend.add(subUpdates);
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }

  private updateChart(data: DeviceVolumeAndHealthTrendDTO[]) {
    try {
      this.sourceData=data ?? [];

      this.noData=this.sourceData.length===0;

      if (this.noData) {
        this.buildChart               =true;
        this.volumeAndHealthTrendChart=undefined;
        return;
      }

      this.renderChart();
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }

  private renderChart(): void {
    try {
      const data=this.applyDateFilter(this.sourceData);

      this.widgetCategories         =[];
      this.widgetCheckedIn30D       =[];
      this.widgetDeviceCount        =[];
      this.widgetEnrolledLast7D     =[];
      this.widgetMdmCompliant       =[];
      this.widgetMdmCompliantPct    =[];
      this.widgetMoreThan1Dev       =[];
      this.widgetMoreThan1Reg       =[];
      this.widgetNotChecked30D      =[];
      this.widgetNotSupportedEol    =[];
      this.widgetOsMajorCompliant   =[];
      this.widgetOsMajorCompliantPct=[];
      this.widgetOsMinorCompliant   =[];
      this.widgetOsMinorCompliantPct=[];
      this.widgetOver4Years         =[];
      this.widgetStorageThresh      =[];
      this.widgetSupportedEol       =[];

      data.forEach((d, i) => {
        const label=d.enteredDate ? this.formatDate(d.enteredDate) : `Sync ${i + 1}`;
        this.widgetCategories.push(label);

        this.widgetCheckedIn30D       .push(this.toNum(d.checkedIn30Days));
        this.widgetDeviceCount        .push(this.toNum(d.deviceCount));
        this.widgetEnrolledLast7D     .push(this.toNum(d.enrolledLast7Days));
        this.widgetMoreThan1Dev       .push(this.toNum(d.moreThan1Device));
        this.widgetMoreThan1Reg       .push(this.toNum(d.moreThan1Registration));
        this.widgetNotChecked30D      .push(this.toNum(d.notCheckedIn30Days));
        this.widgetNotSupportedEol    .push(this.toNum(d.notSupportedEol));
        this.widgetOver4Years         .push(this.toNum(d.over4YearsOld));
        this.widgetStorageThresh      .push(this.toNum(d.storageThreshold));
        this.widgetSupportedEol       .push(this.toNum(d.supportedEol));
        this.widgetMdmCompliant       .push(this.toNullableNum(d.mdmCompliant));
        this.widgetMdmCompliantPct    .push(this.toPct(d.mdmCompliant,     d.deviceCount));
        this.widgetOsMajorCompliant   .push(this.toNullableNum(d.osMajorCompliant));
        this.widgetOsMajorCompliantPct.push(this.toPct(d.osMajorCompliant, d.deviceCount));
        this.widgetOsMinorCompliant   .push(this.toNullableNum(d.osMinorCompliant));
        this.widgetOsMinorCompliantPct.push(this.toPct(d.osMinorCompliant, d.deviceCount));
      });

      this.hasComplianceData =this.sourceData.some(d => this.toNullableNum(d.mdmCompliant)!=null
                                                        || this.toNullableNum(d.osMajorCompliant)!=null
                                                        || this.toNullableNum(d.osMinorCompliant)!=null);

      if (this.buildChart) {
        this.buildChart=false;
        this.buildVolumeAndHealthTrendChart('["--vz-suresim-blue","--vz-warning","--vz-info","--vz-success","--vz-danger","--vz-secondary","--vz-pink","--vz-orange","--vz-teal","--vz-indigo","#6f42c1","#343a40","#b58900"]');
      }

      this.visibleLegendItems=this.legendItems.filter(item => this.isSeriesAvailable(item.name));

      if (this.chartRef?.updateSeries) {
        this.chartRef.updateSeries(this.buildSeries(), true);

        this.chartRef.updateOptions({
          plotOptions: this.buildPlotOptions(),
          xaxis      : {
            categories: this.widgetCategories,
            type      : 'category'
          },
          yaxis: this.buildYAxis()
        }, false);
      }
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }

  private buildSeries(): any[] {
    try {
      const allSeries=[
        { name: 'Device Count',             data: this.widgetDeviceCount,         type: 'bar'  },
        { name: '> 1 Device',               data: this.widgetMoreThan1Dev,        type: 'line' },
        { name: '> 1 Registration',         data: this.widgetMoreThan1Reg,        type: 'line' },
        { name: 'Checked In (30d)',         data: this.widgetCheckedIn30D,        type: 'line' },
        { name: 'Enrolled (7d)',            data: this.widgetEnrolledLast7D,      type: 'line' },
        { name: 'Not Checked In (30d)',     data: this.widgetNotChecked30D,       type: 'line' },
        { name: 'Not Supported (EOL)',      data: this.widgetNotSupportedEol,     type: 'line' },
        { name: 'Over 4 Years',             data: this.widgetOver4Years,          type: 'line' },
        { name: 'Storage Threshold',        data: this.widgetStorageThresh,       type: 'line' },
        { name: 'Supported (EOL)',          data: this.widgetSupportedEol,        type: 'line' },
        { name: seriesMdmCompliancePct,     data: this.widgetMdmCompliantPct,     type: 'line' },
        { name: seriesOsMajorCompliancePct, data: this.widgetOsMajorCompliantPct, type: 'line' },
        { name: seriesOsMinorCompliancePct, data: this.widgetOsMinorCompliantPct, type: 'line' },
      ];

      const emptyData=this.widgetCategories.map(() => null);

      return allSeries.map(series => (this.isSeriesHidden(series.name) || !this.isSeriesAvailable(series.name))
                                     ? { ...series, data: emptyData }
                                     : series);
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return [];
    }
  }

  private applyDateFilter(data: DeviceVolumeAndHealthTrendDTO[]): DeviceVolumeAndHealthTrendDTO[] {
    try {
      if (this.filterDateFrom==undefined && this.filterDateTo==undefined)
        return data;

      const fromMs=this.filterDateFrom?.date ? this.startOfDayMs(this.filterDateFrom.date) : undefined;
      const toMs  =this.filterDateTo?.date   ? this.endOfDayMs(this.filterDateTo.date)     : undefined;

      return data.filter(d => {
        if (!d.enteredDate)
          return true;

        const entered=new Date(d.enteredDate).getTime();

        if (!Number.isFinite(entered))
          return true;

        if (fromMs!=undefined && entered<fromMs)
          return false;

        if (toMs!=undefined && entered>toMs)
          return false;

        return true;
      });
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return data;
    }
  }

  private endOfDayMs(date: Date): number {
    try {
      const source=new Date(date);
      return new Date(source.getFullYear(), source.getMonth(), source.getDate(), 23, 59, 59, 999).getTime();
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return Number.MAX_SAFE_INTEGER;
    }
  }

  private startOfDayMs(date: Date): number {
    try {
      const source=new Date(date);
      return new Date(source.getFullYear(), source.getMonth(), source.getDate(), 0, 0, 0, 0).getTime();
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return 0;
    }
  }

  public unsubscribeVolumeAndHealthTrendChart() {
    try {
      if (this.subVolumeAndHealthTrend) {
        this.subVolumeAndHealthTrend.unsubscribe();
        this.subVolumeAndHealthTrend=undefined;
      }
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }

  private addSubtitleMarker(chartCtx: any): void {
    try {
      const svgNS     ='http://www.w3.org/2000/svg';
      const subtitleEl=chartCtx.el?.querySelector('.apexcharts-subtitle-text') as SVGTextElement | null;

      if (!subtitleEl) return;

      chartCtx.el.querySelector('.apexcharts-subtitle-marker')?.remove();

      const bbox  =subtitleEl.getBBox();
      const circle=document.createElementNS(svgNS, 'circle') as SVGCircleElement;
      circle.setAttribute('class', 'apexcharts-subtitle-marker');
      circle.setAttribute('cx',    String(bbox.x + bbox.width + 10));
      circle.setAttribute('cy',    String(bbox.y + bbox.height / 2));
      circle.setAttribute('r',     '5');
      circle.setAttribute('fill',  '#9aa3af');
      subtitleEl.parentElement!.appendChild(circle);
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }

  private buildVolumeAndHealthTrendChart(colors: any) {
    try {
      colors=this.chartConfigService.resolveColours(colors);

      const seriesNames =[...countSeries, ...compliancePctSeries];
      this.legendItems  =colors.map((color: string, i: number) => ({ color, name: seriesNames[i] ?? '' }));
      const strokeWidths=[0, 2, 2, 2, 2, 2, 2, 2, 2, 2, 3, 3, 3];

      this.volumeAndHealthTrendChart={
        chart: {
          events : {
            dataPointMouseEnter: (event: any, chartCtx: any, config: any) => { this.setHoveredSeries(seriesNames[config?.seriesIndex ?? -1]); },
            dataPointMouseLeave: ()                                       => { this.setHoveredSeries(undefined); },
            mounted            : (chartCtx: any)                          => { this.addSubtitleMarker(chartCtx); },
            updated            : (chartCtx: any)                          => { this.addSubtitleMarker(chartCtx); }
          },
          height : this.chartHeight,
          pan    : { enabled: false },
          toolbar: { show: false },
          type   : 'line',
          zoom   : { enabled: false },
        },
        colors    : colors,
        dataLabels: {
          enabledOnSeries: [0],
          offsetY        : 4,
          style          : { colors: ['#fff'], fontSize: '11px', fontWeight: 600 }
        },
        fill  : {
          opacity: [1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1]
        },
        grid  : {
          padding: { bottom: 5 }
        },
        legend: {
          show: false
        },
        markers: {
          hover: { size: 10 },
          size : [0, 7, 7, 7, 7, 7, 7, 7, 7, 7, 6, 6, 6]
        },
        // plotOptions: {
        //   bar: { columnWidth: '60%' }
        // },
        plotOptions: this.buildPlotOptions(),
        series     : this.buildSeries(),
        stroke     : {
          curve    : 'smooth',
          dashArray: [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 6, 6, 6],
          width    : strokeWidths
        },
        subtitle: {
          align  : 'center',
          offsetY: -5,
          style  : { color: '#9aa3af', fontSize: '11px' },
          text   : 'Hover over a bar or line marker to view its details'
        },
        title: {
          style: { fontWeight: 600 },
          text : undefined
        },
        tooltip: {
          intersect: true,
          shared   : false,
          y        : {
            formatter: (value: number, opts: any) => this.formatTooltipValue(value, opts)
          }
        },
        xaxis: {
          categories: this.widgetCategories,
          labels    : { rotate: -90, rotateAlways: true },
          title     : { offsetY: 5, style: { fontWeight: 600 }, text: 'Last Sync' },
          type      : 'category'
        },
        yaxis: this.buildYAxis()
      };
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }

  private setHoveredSeries(name: string | undefined): void {
    try {
      if (this.hoveredSeriesName===name)
        return;

      this.ngZone.run(() => { this.hoveredSeriesName=name; });
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }

  private formatDate(isoDate: string): string {
    try {
      const d  =new Date(isoDate);
      const pad=(n: number) => n.toString().padStart(2, '0');
      return `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
    }
    catch {
      return isoDate;
    }
  }

  private toNum(value: number | undefined): number {
    const n=Number(value);
    return Number.isFinite(n) ? n : 0;
  }

  private toNullableNum(value: number | null | undefined): number | null {
    try {
      if (value==null)
        return null;

      const n=Number(value);
      return Number.isFinite(n) ? n : null;
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return null;
    }
  }

  private toPct(count: number | null | undefined, deviceCount: number | undefined): number | null {
    try {
      const c    =this.toNullableNum(count);
      const total=this.toNullableNum(deviceCount);

      if (c==null || total==null || total<=0)
        return null;

      return Math.round((c/total)*1000)/10;
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return null;
    }
  }

  private countAxisMax(): number | undefined {
    try {
      const values=this
                   .buildSeries()
                   .filter(series => countSeries.includes(series.name))
                   .flatMap(series => <(number | null)[]>series.data)
                   .filter((value): value is number => value!=null && Number.isFinite(value));

      const max=values.length ? Math.max(...values) : 0;

      if (max<=0)
        return undefined;

      const magnitude=Math.pow(10, Math.floor(Math.log10(max)));
      const step     =magnitude/2;

      return Math.ceil(max/step)*step;
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return undefined;
    }
  }

  private buildPlotOptions(): any {
    try {
      return {
        bar: {
          borderRadius           : 6,
          borderRadiusApplication: 'end',
          columnWidth            : this.widgetCategories.length>12 ? '60%' : '40px',
          dataLabels             : { position: 'top' }
        }
      };
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return { bar: { columnWidth: '60%' } };
    }
  }

  private buildYAxis(): any {
    try {
      const anyCountVisible=countSeries.some(name => !this.isSeriesHidden(name));
      const countMax       =this.countAxisMax();
      const countAxis      ={
        labels    : { formatter: (value: number) => Math.round(value).toLocaleString() },
        max       : countMax,
        min       : 0,
        seriesName: countSeries,
        show      : anyCountVisible || !this.hasComplianceData,
        tickAmount: (countMax!=null && countMax<=10) ? countMax : undefined,
        title     : { style: { fontWeight: 600 }, text: 'Count' }
      };

      if (!this.hasComplianceData)
        return [{ ...countAxis, seriesName: [...countSeries, ...compliancePctSeries] }];

      return [
        countAxis,
        {
          labels    : { formatter: (value: number) => `${Math.round(value)}%` },
          max       : 100,
          min       : 0,
          opposite  : true,
          seriesName: compliancePctSeries,
          tickAmount: 5,
          title     : { style: { fontWeight: 600 }, text: '% Compliant' }
        }
      ];
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return { title: { text: 'Count' } };
    }
  }

  private formatTooltipValue(value: number, opts: any): string {
    try {
      if (value==null)
        return "";

      const pointIndex=opts?.dataPointIndex ?? -1;
      const counts    =[this.widgetMdmCompliant, this.widgetOsMajorCompliant, this.widgetOsMinorCompliant];
      const pctIndex  =(opts?.seriesIndex ?? -1)-countSeries.length;

      if (pctIndex<0 || pctIndex>=counts.length)
        return Number(value).toLocaleString();

      const count=counts[pctIndex][pointIndex];
      const total=this.widgetDeviceCount[pointIndex];

      return (count==null || total==null)
             ? `${value}%`
             : `${value}% (${count.toLocaleString()} of ${total.toLocaleString()})`;
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return String(value);
    }
  }

  private isSeriesAvailable(name: string): boolean {
    try {
      if (compliancePctSeries.indexOf(name)===-1)
        return true;

      if (!this.hasComplianceData)
        return false;

      return !(this.isOtherClass && mobileOnlySeries.indexOf(name)!==-1);
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return true;
    }
  }

  public clearDateRange(): void {
    try {
      this.filterDateFrom=undefined;
      this.filterDateTo  =undefined;
      this.quickRangeDays=undefined;

      this.renderChart();
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }

  public dateFromChanged(date: OurDate | undefined): void {
    try {
      this.filterDateFrom=date;
      this.quickRangeDays=undefined;

      this.renderChart();
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }

  public dateToChanged(date: OurDate | undefined): void {
    try {
      this.filterDateTo  =date;
      this.quickRangeDays=undefined;

      this.renderChart();
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }

  public isLegendGroupEnd(name: string): boolean {
    try {
      return name===countSeries[0] || name===countSeries[countSeries.length-1];
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return false;
    }
  }

  public isSeriesHidden(name: string): boolean {
    try {
      return this.hiddenSeries.indexOf(name)!==-1;
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return false;
    }
  }

  public selectQuickRange(days: number): void {
    try {
      if (this.quickRangeDays===days) {
        this.clearDateRange();
        return;
      }

      const to  =new Date();
      const from=new Date();
      from.setDate(from.getDate()-days);

      this.filterDateFrom=populateOurDate(this.exceptionsService, from);
      this.filterDateTo  =populateOurDate(this.exceptionsService, to);
      this.quickRangeDays=days;

      this.renderChart();
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }

  public showAllSeries(): void {
    try {
      this.hiddenSeries=[];

      this.renderChart();
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }

  public focusSeries(name: string | undefined): void {
    try {
      this.hoveredSeriesName=name;

      const root=this.chartHost?.nativeElement;

      if (!root)
        return;

      const focusIndex=name==undefined ? -1 : [...countSeries, ...compliancePctSeries].indexOf(name);
      const elements  =root.querySelectorAll<SVGElement>('.apexcharts-series, .apexcharts-datalabels, .apexcharts-series-markers-wrap');

      elements.forEach(el => {
        const realIndex=el.getAttribute('data:realIndex');

        if (realIndex==null)
          return;

        el.style.transition='opacity 0.15s ease-in-out';
        el.style.opacity   =(focusIndex<0 || Number(realIndex)===focusIndex || Number(realIndex)===0) ? '' : '0';
      });
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }

  public toggleSeries(name: string): void {
    try {
      const index=this.hiddenSeries.indexOf(name);

      if (index===-1)
        this.hiddenSeries.push(name);
      else
        this.hiddenSeries.splice(index, 1);

      this.renderChart();
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }

  public isolateSeries(name: string): void {
    try {
      const others=this.visibleLegendItems.filter(item => item.name!==name).map(item => item.name);
      const alreadyIsolated=this.hiddenSeries.length===others.length
                            && others.every(other => this.isSeriesHidden(other));

      this.hiddenSeries=alreadyIsolated ? [] : others;

      this.renderChart();
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }

  public toggleContainerExpansion(): void {
    try {
      this.expandContainer=!this.expandContainer;

      if (this.expandContainer) {
        setTimeout(() => {
          try {
            window.dispatchEvent(new Event('resize'));
          }
          catch(e) {
            this.exceptionsService.handleException(<Error>e);
          }
        }, 0);
      }
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }

  public collapseContainer(): void {
    try {
      this.expandContainer=false;
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }

  public closeContainer(): void {
    try {
      this.visibleContainer=false;
      this.containerClosed.emit();
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }
}
